<script setup lang="ts">
import { localize } from "@/utils";
import { computed, reactive, ref } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Swiper as SwiperClass } from "swiper/types";
import { Navigation, Pagination, Autoplay } from "swiper/modules";

import HomeHeader from "@/views/home/HomeHeader.vue";
import HomeFooter from "@/views/home/HomeFooter.vue";
import { useLocalizationStore } from "@/store";

const textSwiperRef = ref<SwiperClass | null>(null);
const scrollbarSwiperRef = ref<SwiperClass | null>(null);
const localizationStore = useLocalizationStore();

// window.scrollTo(0, 0);
const state = reactive({
  tabIndex: 0,
  textSwiperIndex: 0,
  scrollbarIndex: 0,
  autoplay: true,
});
const textSwiperEvent = {
  init: function (swiper: SwiperClass) {
    textSwiperRef.value = swiper;
  },
  slideChange: function (swiper: SwiperClass) {
    state.textSwiperIndex = swiper.realIndex;
  },
};
const scrollbarSwiperEvent = {
  init: function (swiper: SwiperClass) {
    scrollbarSwiperRef.value = swiper;
    swiper.autoplay.start();
  },
  slideChange: function (swiper: SwiperClass) {
    state.scrollbarIndex = swiper.realIndex;
  },
};

function slideTextSwiper(index: number) {
  textSwiperRef.value?.slideToLoop(index);
}

const termsLink = computed(() => {
  return localizationStore.getLanguage() === "ko-KR"
    ? "https://boot-static-assets.r2playground.com/download/docs/pingpong_partner_manual_kr.pdf"
    : "https://boot-static-assets.r2playground.com/download/docs/pingpong_partner_manual_en.pdf";
});
</script>

<template>
  <div id="wrap">
    <home-header />

    <!-- container -->
    <main id="ct">
      <!-- 페이지 시작 -->
      <div class="illust-intro center">
        <div class="container">
          <h1 class="heading">R2 Center</h1>
          <span>Your Digital Expert Group.</span>
          <p
            class="fs-4"
            v-html="
              localize('R2 Center는 디지털에 대한 오랜 노하우와 최첨단 장비를 갖춘 디지털 전문 랩입니다.<br/>지금 가까운 R2 Center를 검색하고, 바로 온라인 오더를 시작해 보세요.<br/>전문 센터만의 하이 퀄리티, 직접 경험해 보세요.')
            "
          />
          <div class="illust">
            <img src="@/assets/home/img/playground/r2center-illust.png" alt="" />
          </div>
        </div>
      </div>
      <div class="container">
        <ul class="nav nav-tabs r2-tabs blue">
          <li class="nav-item">
            <a data-toggle="tab" class="nav-link" @click="state.tabIndex = 0" :class="{ active: state.tabIndex == 0 }"
              >R2 Center Service</a
            >
          </li>
          <li class="nav-item">
            <a
              data-toggle="tab"
              class="nav-link"
              @click="state.tabIndex = 1"
              :class="{ active: state.tabIndex == 1 }"
              >{{ localize("R2 Center 소개") }}</a
            >
          </li>
        </ul>
        <div class="tab-content">
          <div id="tab01" class="tab-pane" :class="{ active: state.tabIndex == 0 }">
            <section class="sect">
              <div class="subtitle-wrap">
                <h2 class="font-poppins">Enhance your Practice<br />with Global Digital Experts</h2>
                <p>{{ localize("R2 Center, 디지털 전문가들의 특별한 서비스를 경험해 보세요.") }}</p>
              </div>
              <ul class="swiper-nav">
                <li :class="{ active: state.textSwiperIndex == 0 }" @click="slideTextSwiper(0)">
                  <i class="icon-r2-implant-planning"></i>R2 Guide Service
                </li>
                <li :class="{ active: state.textSwiperIndex == 1 }" @click="slideTextSwiper(1)">
                  <i class="icon-r2-implant-planning"></i>Dental Prosthesis
                </li>
                <li :class="{ active: state.textSwiperIndex == 2 }" @click="slideTextSwiper(2)">
                  <i class="icon-meg-aligner-planning"></i>Meg Aligner
                </li>
                <li :class="{ active: state.textSwiperIndex == 3 }" @click="slideTextSwiper(3)">
                  <i class="icon-faceguide-planning"></i>FaceGIDE
                </li>
                <li :class="{ active: state.textSwiperIndex == 4 }" @click="slideTextSwiper(4)">
                  <i class="icon-mxfacial"></i>Mx.Facial &<br />Plastic Reconstruction
                </li>
              </ul>
              <swiper
                class="text-pagination-slider"
                :loop="true"
                :observer="true"
                :observe-parents="true"
                :modules="[Navigation, Pagination]"
                :navigation="{
                  nextEl: '.swiper-button-next',
                  prevEl: '.swiper-button-prev',
                }"
                :pagination="{
                  el: '.swiper-pagination',
                  type: 'bullets',
                }"
                @init="textSwiperEvent.init"
                @slideChange="textSwiperEvent.slideChange"
              >
                <swiper-slide>
                  <div class="swiper-img">
                    <img src="@/assets/home/img/playground/r2center1-1.png" alt="" />
                  </div>
                  <div class="pagination-slide-txt">
                    <h3>R2 Guide & One-Day Implant</h3>
                    <p
                      v-html="
                        localize('R2GATE 임플란트 플래닝을 기반으로<br/>수술용 R2 Guide와 Immediate Loading을 위한 템포러리 크라운을 제공합니다.')
                      "
                    />
                    <div class="list-wrap">
                      <span>What's included</span>
                      <ul class="list-dot no-grid">
                        <li>R2GATE Implant planning</li>
                        <li>R2 Guide</li>
                        <li>Temporary Crown (Option)</li>
                      </ul>
                    </div>
                  </div>
                </swiper-slide>
                <swiper-slide>
                  <div class="swiper-img">
                    <img src="@/assets/home/img/playground/r2center1-2.png" alt="" />
                  </div>
                  <div class="pagination-slide-txt">
                    <h3>Dental Prosthesis</h3>
                    <p v-html="localize('임플란트. 자연치, 심미, 무치악 보철까지<br/>다양한 CAD/CAM 보철물을 서비스해 드립니다.')"
                    />
                    <div class="list-wrap">
                      <span>What's included</span>
                      <ul class="list-dot">
                        <li>Crown & Bridge</li>
                        <li>Customized Abutment</li>
                        <li>Bar-Bridge</li>
                        <li>Full moth restoration</li>
                        <li>Digital Denture</li>
                      </ul>
                    </div>
                  </div>
                </swiper-slide>
                <swiper-slide>
                  <div class="swiper-img">
                    <img src="@/assets/home/img/playground/r2center1-3.png" alt="" />
                  </div>
                  <div class="pagination-slide-txt">
                    <h3>Meg Aligner</h3>
                    <p v-html="localize('임플란트, 보철 치료는 물론<br/>성인 심미교정을 위한 투명교정 솔루션, Meg Aligner를 제공합니다.')"
                    />
                    <span style="font-size: 15px; font-weight: 500;">
                      {{ localize("⊛ 국내 Meg Aligner 서비스는 메가젠임플란트에서 운영합니다.") }}
                    </span>
                    <div class="list-wrap">
                      <span>What's included</span>
                      <ul class="list-dot  no-grid">
                        <li>Meg Aligner setup file</li>
                        <li>{{ localize("Meg Aligner 투명 교정 장치") }}</li>
                      </ul>
                    </div>
                    <!-- <button class="btn btn-primary mt-4 d-flex justify-content-center">{{ localize("주문하기") }}</button> -->
                  </div>
                </swiper-slide>
                <swiper-slide>
                  <div class="swiper-img">
                    <img src="@/assets/home/img/playground/r2center1-4.png" alt="" />
                  </div>
                  <div class="pagination-slide-txt">
                    <h3>FaceGIDE</h3>
                    <p
                      v-html="
                        localize('디지털 양악 수술을 위한 수술 계획과<br/>환자 맞춤형 Saw Guide 및 Plate를 제작해 드립니다.')
                      "
                    />
                    <span style="font-size: 15px; font-weight: 500;">
                      {{ localize("⊛ FaceGIDE는 메가젠임플란트가 직접 제작하고 공급합니다.") }}
                    </span>
                    <div class="list-wrap">
                      <span>What's included</span>
                      <ul class="list-dot no-grid">
                        <li>{{ localize("양악수술을 위한 분석") }}</li>
                        <li>{{ localize("Jaw segmentation 및 setup (file)") }}</li>
                        <li>Saw-guide</li>
                        <li>{{ localize("환자 맞춤형 Plate") }}</li>
                      </ul>
                    </div>
                    <div class="d-flex">
                      <a
                        href="https://playgroundmaintenance.blob.core.windows.net/webstaticassets/FaceGIDE/FACEGIDE Order GuideBook.pdf"
                        class="btn btn-primary mt-4 d-flex justify-content-center mr-4"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{ localize("주문 가이드 보기") }}
                      </a>
                      <!-- <button class="btn btn-primary mt-4 d-flex justify-content-center">{{ localize("주문하기") }}</button> -->
                    </div>
                  </div>
                </swiper-slide>
                <swiper-slide>
                  <div class="swiper-img">
                    <img src="@/assets/home/img/playground/r2center1-5.png" alt="" />
                  </div>
                  <div class="pagination-slide-txt">
                    <h3>
                      Mx.Facial & <br />
                      Plastic Reconstruction
                    </h3>
                    <p v-html="localize('선천성 기형,외상,질병 등으로 손실된 안면부를 복원하기 위한<br/>환자 맞춤형 특수 보형물을 제작합니다.')">
                    </p>
                    <span style="font-size: 15px; font-weight: 500;">
                      {{ localize("⊛ 악안면 보형물 서비스는 메가젠임플란트에서 운영합니다.") }}
                    </span>
                    <div class="list-wrap">
                      <span>What's included</span>
                      <ul class="list-dot" style="font-size: 18px">
                        <li>{{ localize('CBCT 기반 악안면 분석') }}</li>
                        <li>{{ localize('환자 맞춤형 보형물 (PEEK, Titanium)') }}</li>
                        <li>{{ localize('환자 맞춤형 보형물 디자인') }}</li>
                        <li>{{ localize('환자 맞춤형 보형물 적용을 위한 가이드') }}</li>
                      </ul>
                    </div>
                    <div class="d-flex">
                      <!-- <button class="btn btn-primary mt-4 d-flex justify-content-center mr-4">{{ localize("주문 가이드 보기") }}</button>
                      <button class="btn btn-primary mt-4 d-flex justify-content-center">{{ localize("주문하기") }}</button> -->
                    </div>
                  </div>
                </swiper-slide>
                <div class="swiper-pagination pagination_bullet"></div>
                <div class="swiper-controls type-line">
                  <div class="swiper-button-prev"></div>
                  <div class="swiper-button-next"></div>
                </div>
              </swiper>
            </section>
          </div>
          <div id="tab02" class="tab-pane" :class="{ active: state.tabIndex == 1 }">
            <div class="subtitle-wrap">
              <h2 class="font-poppins">Digital Expert Guru!</h2>
              <!-- 다국어 누락 -->
              <p
                class="desc"
                v-html="
                  localize('R2 Center는 메가젠과 함께 디지털 혁신을 실현해 나가는 디지털 전문 랩입니다.<br/>CAD/CAM 기반의 보철물부터 환자 맞춤형 보형물까지,<br/>고품질의 서비스를 제공합니다.')
                "
              />
            </div>
            <!-- 다국어 누락 -->
            <div class="timeline">
              <ul>
                <li>
                  <img src="@/assets/home/img/playground/r2center3-1.png" alt="" />
                  <div class="btn-desc">
                    <h3>{{ localize("R2 Center 찾아보기") }}</h3>
                    <p>
                      {{ localize("현재 활동중인 R2 Center를 찾아 보세요.") }}
                    </p>
                    <router-link to="/home/playground/r2center" class="btn btn-primary" style="pointer-events: none;" @click.prevent="false"
                      >{{ localize("준비 중") }}<i class="icon icon-arrow"></i
                    ></router-link>
                  </div>
                </li>
                <li style="margin-bottom: -100px">
                </li>
              </ul>
              <div class="recruit-section">
                <h2>{{ localize("지금 R2 Center에 지원하세요") }}</h2>
                <p class="fs-4" v-html="localize('디지털 혁신에 함께할 파트너 랩을 찾습니다.<br/>메가젠과 함께, 디지털 덴티스트리의 내일을 열어주세요.')" />
                <div class="recruit-section-content">
                  <img src="@/assets/home/img/playground/r2center2-1.png" class="w-50"/>
                  <div class="w-50">
                    <div class="recruit-section-title"
                      v-html="
                        localize('R2 Center 가입은<br/>다음 단계를 따라 진행됩니다.')
                      "
                    />
                    <div>
                      <div class="text-muted my-1 fs-4">① {{ localize("R2 Playground 회원가입") }}</div>
                      <div class="text-muted my-1 fs-4">② {{ localize("Partner home web 서비스 및 프로필 등록") }}</div>
                      <div class="text-muted my-1 fs-4">③ {{ localize("Ping Pong 설치") }}</div>
                      <div class="text-muted my-1 fs-4">④ {{ localize("Ping Pong 로그인") }}</div>
                      <div class="text-muted my-1 fs-4">⑤ {{ localize("player에게 주문 받기") }}</div>
                    </div>
                    <div class="d-flex" style="gap: 20px;">
                      <a class="btn btn-primary mt-4" href="/home/join/agreement/centers">{{ localize("R2 Center 회원가입") }}<i class="icon icon-arrow"></i></a>
                      <a class="btn btn-primary mt-4" :href="termsLink" target="_blank">Partner web manual<i class="icon icon-arrow"></i></a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- //container -->
    </main>
    <!-- footer -->
    <home-footer />
    <!-- //footer -->

    <div class="bg-blur"></div>
  </div>
</template>

<style scoped>
.recruit-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 200px;
}
.recruit-section p {
  margin: 25px 0;
  text-align: center;
}
.recruit-section h2 {
  font-weight: 700;
  font-size: 3.333rem;
}
.recruit-section-content {
  display: flex;
  justify-content: start;
  gap:50px;
  margin: 40px 0 40px 0;
}
.recruit-section-content .recruit-section-title {
  text-align: left;;
  font-weight: 700;
  font-size: 40px;
  line-height: 140%;
  color: #151131;
  letter-spacing: -3%;
}
</style>
